import classNames from 'classnames';
import { ReactNode } from 'react';

interface Props {
  /** Optional group eyebrow (e.g. "Web", "Mobile", "Desktop"). */
  label?: string;
  /** Anchor id used by the download hero jump links. */
  id?: string;
  /** Platform glyph shown in the rounded icon tile. */
  icon: ReactNode;
  /** Small brand tag above the title (e.g. "MindFeed for Web"). */
  tag: string;
  /** Optional pill shown next to the tag (e.g. a "Soon" badge). */
  badge?: ReactNode;
  /** Row title; may include an `<span>` highlight. */
  title: ReactNode;
  /** Supporting paragraph. */
  description: string;
  /** Feature checklist (each rendered with a green check). */
  features: string[];
  /** CTA(s) — a CtaButton, StoreBadge, or both. */
  actions: ReactNode;
  /** Device / product visual for the other column. */
  visual: ReactNode;
  /** Flip the columns on desktop (visual on the left). */
  reversed?: boolean;
}

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

// One platform showcase row on the /download page (copy + device visual).
// Mirrors the marketing design's `.prow` — a two-column band that alternates
// orientation down the page.
export const PlatformRow = ({
  label,
  id,
  icon,
  tag,
  badge,
  title,
  description,
  features,
  actions,
  visual,
  reversed = false,
}: Props) => (
  <section id={id} className={styles.section}>
    <div className={styles.wrap}>
      {label && (
        <div className={styles.label.container}>
          <span className={styles.label.text}>{label}</span>
          <span className={styles.label.line} />
        </div>
      )}

      <div className={styles.row}>
        <div className={styles.copy}>
          <span className={styles.icon}>{icon}</span>
          <div className={styles.tag}>
            {tag}
            {badge && <span className="ml-2">{badge}</span>}
          </div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.description}>{description}</p>
          <div className={styles.feats}>
            {features.map((feature) => (
              <div key={feature} className={styles.feat}>
                <span className={styles.featIcon}>
                  <CheckIcon />
                </span>
                {feature}
              </div>
            ))}
          </div>
          <div className={styles.actions}>{actions}</div>
        </div>

        <div className={classNames(styles.vis, reversed && 'lg:order-first')}>
          <span className={styles.visGlow} aria-hidden="true" />
          {visual}
        </div>
      </div>
    </div>
  </section>
);

const styles = {
  // scroll-mt keeps the section clear of the fixed nav when jumped to from the
  // download hero's platform links.
  section: 'scroll-mt-[110px] marketing-dark:bg-mf-navy pt-10',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  label: {
    container: 'mb-2 flex items-center gap-4',
    text: 'whitespace-nowrap text-[12.5px] font-bold uppercase tracking-[0.18em] text-mf-gray-500 marketing-dark:text-[#7fa8ff]',
    line: classNames(
      'h-px flex-1',
      'bg-[linear-gradient(90deg,#DADADA,transparent)]',
      'marketing-dark:bg-[linear-gradient(90deg,rgba(127,168,255,0.4),transparent)]',
    ),
  },
  row: classNames(
    'grid grid-cols-1 items-center gap-9 py-[38px] text-center',
    'lg:grid-cols-[1fr_1.08fr] lg:gap-[62px] lg:py-[46px] lg:text-left',
  ),
  copy: 'mx-auto lg:mx-0',
  icon: classNames(
    'mx-auto mb-[18px] inline-flex h-[50px] w-[50px] items-center justify-center rounded-[14px] lg:mx-0',
    'border border-[#F0F0F0] bg-[#F4F4F4]',
    'marketing-dark:border-white/[0.12] marketing-dark:bg-white/[0.06]',
    '[&>svg]:h-[26px] [&>svg]:w-[26px] [&>svg]:fill-none [&>svg]:stroke-[1.7]',
    '[&>svg]:stroke-mf-off-black-500 marketing-dark:[&>svg]:stroke-white',
    '[&>svg.gly]:stroke-none',
    '[&>svg.gly]:fill-mf-off-black-500 marketing-dark:[&>svg.gly]:fill-white',
  ),
  tag: 'mb-4 text-[13px] font-semibold tracking-[0.04em] text-mf-gray-500 marketing-dark:text-[#9cc0ff]',
  title:
    'mb-2 text-[32px] font-extrabold leading-[1.12] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[36px]',
  description:
    'mx-auto mb-[22px] max-w-[430px] text-[17px] leading-[1.62] text-mf-gray-600 marketing-dark:text-white/66 lg:mx-0',
  feats: 'mb-[26px] flex flex-col items-center gap-[11px] lg:items-start',
  feat: 'flex items-center gap-[11px] text-[15px] font-medium text-mf-off-black-500 marketing-dark:text-white/82',
  featIcon: classNames(
    'flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full',
    'bg-[#2BA471]/15 marketing-dark:bg-[#6ed7a6]/[0.14]',
    '[&>svg]:h-3 [&>svg]:w-3 [&>svg]:fill-none [&>svg]:stroke-[2.4]',
    '[&>svg]:stroke-[#2BA471] marketing-dark:[&>svg]:stroke-[#6ed7a6]',
  ),
  actions: 'flex flex-wrap items-center justify-center gap-3 lg:justify-start',
  vis: 'relative flex min-h-[380px] items-center justify-center',
  visGlow: classNames(
    'pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[540px] -translate-x-1/2 -translate-y-1/2 rounded-full',
    'bg-[radial-gradient(circle,rgba(189,222,255,0.5),transparent_64%)]',
    'marketing-dark:bg-[radial-gradient(circle,rgba(40,80,200,0.3),transparent_64%)]',
  ),
};
